import type { HistoryMode } from "@/features/history/historyMode";
import {
  createMediator,
  type Mediator,
  type MediatorTransitionResult,
  type TypedEvent,
} from "@/shared/lib/componentHierarchy";

export const SIDE_MENU_DRAG_CLOSE_OFFSET = -100;
export const SIDE_MENU_DRAG_CLOSE_VELOCITY = -500;

export interface SideMenuState {
  readonly historyMode: HistoryMode;
}

export type SideMenuEvent =
  | { readonly type: "side-menu.close-requested" }
  | {
      readonly type: "side-menu.history-mode-selected";
      readonly mode: HistoryMode;
    }
  | { readonly type: "side-menu.history-refresh-requested" }
  | {
      readonly type: "side-menu.drag-ended";
      readonly offsetX: number;
      readonly velocityX: number;
    };

export type SideMenuInputEvent =
  | { readonly type: "side-menu.close-requested" }
  | {
      readonly type: "side-menu.history-mode-selected";
      readonly mode: HistoryMode;
    }
  | { readonly type: "side-menu.history-refresh-requested" }
  | {
      readonly type: "side-menu.drag-ended";
      readonly offsetX: number;
      readonly velocityX: number;
    };

export type SideMenuEffect =
  | { readonly type: "close" }
  | { readonly type: "refresh-history" };

export interface SideMenuMediatorOptions {
  readonly runEffect?: (effect: SideMenuEffect) => void;
  readonly initialHistoryMode?: HistoryMode;
  readonly getHistoryState?: () => { itemsCount: number; isFetching: boolean };
}

export function isSideMenuEvent(event: TypedEvent): event is SideMenuEvent {
  switch (event.type) {
    case "side-menu.close-requested":
    case "side-menu.history-refresh-requested":
      return true;
    case "side-menu.history-mode-selected":
      return isHistoryMode(event);
    case "side-menu.drag-ended":
      return (
        hasNumberField(event, "offsetX") && hasNumberField(event, "velocityX")
      );
    default:
      return false;
  }
}

export function transitionSideMenu(
  state: SideMenuState,
  event: SideMenuEvent,
): MediatorTransitionResult<SideMenuState, SideMenuEffect> {
  switch (event.type) {
    case "side-menu.close-requested":
      return {
        status: "handled",
        state,
        effects: [{ type: "close" }],
      };
    case "side-menu.history-mode-selected":
      return {
        status: "handled",
        state:
          event.mode === state.historyMode
            ? state
            : { historyMode: event.mode },
      };
    case "side-menu.history-refresh-requested":
      return {
        status: "handled",
        state,
        effects: [{ type: "refresh-history" }],
      };
    case "side-menu.drag-ended":
      return {
        status: "handled",
        state,
        effects:
          event.offsetX < SIDE_MENU_DRAG_CLOSE_OFFSET ||
          event.velocityX < SIDE_MENU_DRAG_CLOSE_VELOCITY
            ? [{ type: "close" }]
            : [],
      };
  }
}

export function createSideMenuMediator(
  options: SideMenuMediatorOptions = {},
  restoredState?: SideMenuState,
): Mediator<SideMenuState, SideMenuEvent, SideMenuEffect> {
  return createMediator<SideMenuState, SideMenuEvent, SideMenuEffect>(
    {
      historyMode:
        restoredState?.historyMode ?? options.initialHistoryMode ?? "viewed",
    },
    (state, event) => {
      if (
        event.type === "side-menu.history-refresh-requested" &&
        options.getHistoryState
      ) {
        const history = options.getHistoryState();
        if (history.itemsCount === 0 || history.isFetching)
          return { status: "handled", state };
      }
      return transitionSideMenu(state, event);
    },
    { runEffect: options.runEffect },
  );
}

function isHistoryMode(
  event: TypedEvent,
): event is TypedEvent & { readonly mode: HistoryMode } {
  const mode = (event as Record<string, unknown>).mode;
  return mode === "viewed" || mode === "posted";
}

function hasNumberField(event: TypedEvent, field: string): boolean {
  return typeof (event as Record<string, unknown>)[field] === "number";
}
